export const SHIFT_BLOCKS = [
  {
    id: 'morning',
    nameKey: 'shiftMorning',
    timeRange: '07:00 – 14:00',
    startMinutes: 7 * 60,
    endMinutes: 14 * 60,
  },
  {
    id: 'afternoon',
    nameKey: 'shiftAfternoon',
    timeRange: '14:00 – 20:00',
    startMinutes: 14 * 60,
    endMinutes: 20 * 60,
  },
  {
    id: 'night',
    nameKey: 'shiftNight',
    timeRange: '20:00 – 07:00',
    startMinutes: 20 * 60,
    endMinutes: 7 * 60,
  },
];

function minutesOfDay(date) {
  return date.getHours() * 60 + date.getMinutes();
}

export function getActiveShiftBlockId(now = new Date()) {
  const minutes = minutesOfDay(now);
  if (minutes >= 7 * 60 && minutes < 14 * 60) return 'morning';
  if (minutes >= 14 * 60 && minutes < 20 * 60) return 'afternoon';
  return 'night';
}

export function getOrderedShiftBlocks(now = new Date()) {
  const activeId = getActiveShiftBlockId(now);
  const activeIndex = SHIFT_BLOCKS.findIndex((block) => block.id === activeId);
  if (activeIndex <= 0) return [...SHIFT_BLOCKS];
  return [
    ...SHIFT_BLOCKS.slice(activeIndex),
    ...SHIFT_BLOCKS.slice(0, activeIndex),
  ];
}

function isInBlock(minutes, block) {
  if (block.startMinutes < block.endMinutes) {
    return minutes >= block.startMinutes && minutes < block.endMinutes;
  }
  // Night wraps midnight: 20:00–07:00
  return minutes >= block.startMinutes || minutes < block.endMinutes;
}

export function getShiftBlockIdForTask(task, now = new Date()) {
  const activeId = getActiveShiftBlockId(now);

  // PRN / no fixed time → current active section
  if (task?.isPrn || !task?.dueTime) {
    return activeId;
  }

  const due = new Date(task.dueTime);
  if (Number.isNaN(due.getTime())) return activeId;

  const minutes = minutesOfDay(due);
  const match = SHIFT_BLOCKS.find((block) => isInBlock(minutes, block));
  return match?.id || activeId;
}

function dueSortKey(task) {
  if (!task?.dueTime) return Number.MAX_SAFE_INTEGER;
  const due = new Date(task.dueTime);
  if (Number.isNaN(due.getTime())) return Number.MAX_SAFE_INTEGER;
  return minutesOfDay(due);
}

function sortSectionTasks(tasks) {
  const incomplete = [];
  const complete = [];

  tasks.forEach((task) => {
    if (task.status === 'complete') complete.push(task);
    else incomplete.push(task);
  });

  incomplete.sort((a, b) => {
    const diff = dueSortKey(a) - dueSortKey(b);
    if (diff !== 0) return diff;
    return String(a.title || '').localeCompare(String(b.title || ''));
  });

  complete.sort((a, b) => {
    const diff = dueSortKey(a) - dueSortKey(b);
    if (diff !== 0) return diff;
    return String(a.completedAt || '').localeCompare(String(b.completedAt || ''));
  });

  return [...incomplete, ...complete];
}

/**
 * Group tasks into ordered shift sections (active first).
 * Always returns all three sections.
 */
export function groupTasksByShiftBlocks(tasks = [], now = new Date()) {
  const ordered = getOrderedShiftBlocks(now);
  const buckets = Object.fromEntries(ordered.map((block) => [block.id, []]));

  tasks.forEach((task) => {
    const blockId = getShiftBlockIdForTask(task, now);
    if (!buckets[blockId]) buckets[blockId] = [];
    buckets[blockId].push(task);
  });

  return ordered.map((block) => ({
    ...block,
    tasks: sortSectionTasks(buckets[block.id] || []),
  }));
}
